// Real data service for Bitcoin treasury companies and news
// Uses multiple free APIs and paid options

class RealDataService {
  constructor() {
    // API configuration - reads from environment variables first, then allows manual setting
    this.apiKeys = {
      // Free APIs with generous limits - automatically loaded from .env
      polygon: process.env.REACT_APP_POLYGON_API_KEY || null,
      finnhub: process.env.REACT_APP_FINNHUB_API_KEY || null,
      twelve_data: process.env.REACT_APP_TWELVE_DATA_API_KEY || null,
      alpha_vantage: process.env.REACT_APP_ALPHA_VANTAGE_API_KEY || null,
      news_api: process.env.REACT_APP_NEWS_API_KEY || null,
      
      // Professional APIs (paid but better data)
      bloomberg: null,
      reuters: null
    };

    // Clear any existing cached news to force refresh with new filtering
    this.clearNewsCache();
    
    // Also clear localStorage cache to ensure background cache is used
    // this.clearCache(); // Commented out - let cache work normally

    // Cache configuration
    this.cache = {
      news: null,
      newsExpiry: null,
      stockPrices: null,
      stockPricesExpiry: null
    };

    // Cache durations (in milliseconds)
    this.cacheDurations = {
      news: 15 * 60 * 1000, // 15 minutes for news
      stockPrices: 60 * 1000  // 1 minute for stock prices
    };

    // Log which API keys are available (without showing the actual keys)
    console.log('🔑 Available API Keys:', Object.entries(this.apiKeys)
      .filter(([key, value]) => value)
      .map(([key]) => key)
      .join(', ') || 'None configured'
    );

    // Bitcoin treasury companies to track
    this.btcTreasuryTickers = [
      'MSTR', 'MARA', 'RIOT', 'COIN', 'TSLA', 'SQ', 'HUT', 'CLSK', 'SMLR',
      'IREN', 'BITF', 'CORZ', 'CIFR', 'BTBT', 'CAN', 'ARBK', 'GREE'
    ];

    // Treasury-specific keywords - must be very specific to corporate/treasury activity
    this.treasuryKeywords = [
      'bitcoin treasury', 'corporate bitcoin', 'bitcoin reserves', 'bitcoin holdings',
      'bitcoin balance sheet', 'bitcoin acquisition', 'bitcoin accumulation',
      'digital asset strategy', 'crypto treasury', 'bitcoin investment strategy',
      'bitcoin reserves strategy', 'treasury allocation', 'bitcoin portfolio'
    ];

    // Company-specific treasury keywords
    this.companyTreasuryKeywords = [
      'microstrategy bitcoin', 'tesla bitcoin', 'saylor bitcoin', 'mstr bitcoin',
      'marathon digital bitcoin', 'riot platforms bitcoin', 'coinbase bitcoin',
      'square bitcoin', 'block bitcoin', 'bitcoin mining treasury',
      'corporate adoption bitcoin', 'public company bitcoin'
    ];

    // Treasury action keywords
    this.treasuryActionKeywords = [
      'purchase', 'buy', 'acquire', 'add', 'holds', 'treasury', 'reserve',
      'allocation', 'investment', 'strategy', 'adoption', 'balance sheet'
    ];
  }

  // Fetch real stock prices using multiple free sources (FAST parallel approach)
  async fetchRealStockPrices(tickers = this.btcTreasuryTickers) {
    const prices = {};
    const errors = [];
    
    console.log(`🚀 Starting parallel fetch for ${tickers.length} tickers...`);
    
    // Process all tickers in parallel for maximum speed
    const tickerPromises = tickers.map(async (ticker) => {
      return await this.fetchSingleTickerPrice(ticker);
    });
    
    try {
      // Wait for all tickers to complete (with overall timeout)
      const results = await Promise.allSettled(tickerPromises);
      
      results.forEach((result, index) => {
        const ticker = tickers[index];
        
        if (result.status === 'fulfilled' && result.value.success) {
          prices[ticker] = result.value.data;
          console.log(`✓ Got data for ${ticker} from ${result.value.data.source}`);
        } else {
          const error = result.status === 'rejected' ? result.reason.message : result.value.error;
          errors.push(`${ticker}: ${error}`);
          console.warn(`❌ Failed to get data for ${ticker}: ${error}`);
        }
      });
      
    } catch (error) {
      return {
        success: false,
        error: `Parallel fetch failed: ${error.message}`,
        errorCode: 'PARALLEL_FETCH_FAILED',
        data: {}
      };
    }

    if (Object.keys(prices).length === 0) {
      return {
        success: false,
        error: `All price APIs failed. Errors: ${errors.join(' | ')}`,
        errorCode: 'API_ALL_FAILED',
        data: {}
      };
    }

    if (errors.length > 0) {
      return {
        success: true,
        warning: `Some tickers failed: ${errors.join(' | ')}`,
        data: prices
      };
    }

    console.log(`✅ Successfully fetched ${Object.keys(prices).length} prices`);
    return {
      success: true,
      data: prices
    };
  }

  // Fetch single ticker with fast timeout and parallel API racing
  async fetchSingleTickerPrice(ticker) {
    const apiMethods = [
      { name: 'Twelve Data', method: () => this.fetchFromTwelveData(ticker) },
      { name: 'IEX Cloud', method: () => this.fetchFromIEXCloud(ticker) }
    ];

    // Add API key methods if available
    if (this.apiKeys.polygon) {
      apiMethods.unshift({ name: 'Polygon', method: () => this.fetchFromPolygon(ticker) });
    }
    if (this.apiKeys.finnhub) {
      apiMethods.push({ name: 'Finnhub', method: () => this.fetchFromFinnhub(ticker) });
    }
    if (this.apiKeys.alpha_vantage) {
      apiMethods.push({ name: 'Alpha Vantage', method: () => this.fetchFromAlphaVantage(ticker) });
    }

    // Race all APIs against each other - first successful response wins
    const apiPromises = apiMethods.map(async (api) => {
      try {
        const result = await this.withTimeout(api.method(), 5000); // 5 second timeout per API
        if (result) {
          return { success: true, data: result, source: api.name };
        }
        throw new Error('No data returned');
      } catch (error) {
        throw new Error(`${api.name}: ${error.message}`);
      }
    });

    try {
      // Use Promise.allSettled to try all APIs, return first success
      const results = await Promise.allSettled(apiPromises);
      
      // Find first successful result
      for (const result of results) {
        if (result.status === 'fulfilled') {
          return result.value;
        }
      }
      
      // If no success, collect all errors
      const allErrors = results
        .filter(r => r.status === 'rejected')
        .map(r => r.reason.message)
        .join(', ');
      
      return {
        success: false,
        error: `All APIs failed: ${allErrors}`,
        data: null
      };
      
    } catch (error) {
      return {
        success: false,
        error: `API race failed: ${error.message}`,
        data: null
      };
    }
  }

  // Utility function to add timeout to any promise
  withTimeout(promise, timeoutMs) {
    return Promise.race([
      promise,
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error(`Timeout after ${timeoutMs}ms`)), timeoutMs)
      )
    ]);
  }

  // Polygon.io (free tier: 5 calls/minute, 100 calls/month)
  async fetchFromPolygon(ticker) {
    try {
      // Use free tier API key or user's key
      const apiKey = this.apiKeys.polygon || 'kGKgGGLrGfAeFGy0S8Y3_AQqw7FfP2Kz'; // Demo key
      
      // Get current date for market data
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const dateStr = yesterday.toISOString().split('T')[0];
      
      const response = await fetch(
        `https://api.polygon.io/v2/aggs/ticker/${ticker}/range/1/day/${dateStr}/${dateStr}?adjusted=true&sort=asc&limit=1&apikey=${apiKey}`
      );

      if (response.ok) {
        const data = await response.json();
        
        if (data.results && data.results.length > 0) {
          const result = data.results[0];
          const currentPrice = result.c; // close price
          const previousClose = result.o; // open price
          const change = currentPrice - previousClose;
          const changePercent = previousClose > 0 ? (change / previousClose) * 100 : 0;

          return {
            symbol: ticker,
            name: ticker,
            price: currentPrice || 0,
            change: change || 0,
            changePercent: isNaN(changePercent) ? 0 : changePercent,
            volume: result.v || 0,
            timestamp: new Date(),
            source: 'polygon.io'
          };
        }
      } else {
        throw new Error(`Polygon API error: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.warn(`Polygon failed for ${ticker}:`, error);
      throw error;
    }
    
    return null;
  }

  // Twelve Data API (free tier: 800 calls/day)
  async fetchFromTwelveData(ticker) {
    let timeoutId;
    try {
      // Use free tier API key or user's key
      const apiKey = this.apiKeys.twelve_data || 'demo'; // Demo access
      
      // Add timeout with AbortController
      const controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), 4000); // 4 second timeout
      
      const response = await fetch(
        `https://api.twelvedata.com/quote?symbol=${ticker}&apikey=${apiKey}`,
        { signal: controller.signal }
      );
      
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        
        if (data.close && !data.error) {
          const currentPrice = parseFloat(data.close);
          const previousClose = parseFloat(data.previous_close);
          const change = currentPrice - previousClose;
          const changePercent = previousClose > 0 ? (change / previousClose) * 100 : 0;

          return {
            symbol: ticker,
            name: data.name || ticker,
            price: currentPrice || 0,
            change: change || 0,
            changePercent: isNaN(changePercent) ? 0 : changePercent,
            volume: parseInt(data.volume) || 0,
            timestamp: new Date(),
            source: 'twelve_data'
          };
        } else {
          throw new Error(data.message || 'No data available');
        }
      } else {
        throw new Error(`Twelve Data API error: ${response.status}`);
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        console.warn(`Twelve Data timeout for ${ticker}`);
      } else {
        console.warn(`Twelve Data failed for ${ticker}:`, error);
      }
      throw error;
    }
    
    return null;
  }

  // Generate realistic sample data as fallback
  generateSamplePriceData(ticker) {
    const basePrices = {
      'MSTR': 380,
      'MARA': 22,
      'RIOT': 12,
      'COIN': 265,
      'TSLA': 248,
      'SQ': 78,
      'HUT': 24,
      'CLSK': 15
    };
    
    const basePrice = basePrices[ticker] || 50;
    const changePercent = (Math.random() - 0.5) * 10; // ±5% change
    const change = basePrice * (changePercent / 100);
    const currentPrice = basePrice + change;
    
    return {
      symbol: ticker,
      name: `${ticker} Company`,
      price: currentPrice,
      change: change,
      changePercent: changePercent,
      volume: Math.floor(Math.random() * 5000000) + 1000000,
      timestamp: new Date(),
      source: 'sample_data'
    };
  }

  // Fetch from Finnhub (requires free API key)
  async fetchFromFinnhub(ticker) {
    if (!this.apiKeys.finnhub) return null;

    try {
      const response = await fetch(
        `https://finnhub.io/api/v1/quote?symbol=${ticker}&token=${this.apiKeys.finnhub}`
      );

      if (response.ok) {
        const data = await response.json();
        return {
          symbol: ticker,
          name: ticker,
          price: data.c || 0, // current price
          change: data.d || 0, // change
          changePercent: isNaN(data.dp) ? 0 : data.dp, // change percent
          volume: 0,
          timestamp: new Date(),
          source: 'finnhub'
        };
      }
    } catch (error) {
      console.error('Finnhub API error:', error);
    }
    return null;
  }

  // Alpha Vantage (free tier: 25 calls/day)
  async fetchFromAlphaVantage(ticker) {
    try {
      // Use demo key first, users can replace with their own
      const apiKey = this.apiKeys.alpha_vantage || 'demo';
      const response = await fetch(
        `https://www.alphavantage.co/query?function=GLOBAL_QUOTE&symbol=${ticker}&apikey=${apiKey}`
      );

      if (response.ok) {
        const data = await response.json();
        const quote = data['Global Quote'];
        
        if (quote && quote['05. price']) {
          const price = parseFloat(quote['05. price']);
          const change = parseFloat(quote['09. change']);
          const changePercent = parseFloat(quote['10. change percent'].replace('%', ''));
          
          return {
            symbol: ticker,
            name: quote['01. symbol'] || ticker,
            price: isNaN(price) ? 0 : price,
            change: isNaN(change) ? 0 : change,
            changePercent: isNaN(changePercent) ? 0 : changePercent,
            volume: parseInt(quote['06. volume']) || 0,
            timestamp: new Date(),
            source: 'alpha_vantage'
          };
        }
      }
    } catch (error) {
      console.warn('Alpha Vantage API error:', error);
    }
    return null;
  }

  // IEX Cloud (free tier: 500k calls/month)
  async fetchFromIEXCloud(ticker) {
    let timeoutId;
    try {
      // Add timeout with AbortController to prevent hanging requests
      const controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), 4000); // 4 second timeout
      
      const response = await fetch(
        `https://cloud.iexapis.com/stable/stock/${ticker}/quote?token=pk_6c4dee40f4014c7fafaa73c0a36c11d7`,
        { signal: controller.signal }
      );
      
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        
        if (data.latestPrice) {
          return {
            symbol: ticker,
            name: data.companyName || ticker,
            price: data.latestPrice || 0,
            change: data.change || 0,
            changePercent: isNaN(data.changePercent) ? 0 : (data.changePercent * 100),
            volume: data.latestVolume || 0,
            marketCap: data.marketCap,
            timestamp: new Date(),
            source: 'iex_cloud'
          };
        }
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        console.warn(`IEX Cloud timeout for ${ticker}`);
      } else {
        console.warn('IEX Cloud API error:', error);
      }
    }
    return null;
  }

  // Check if cached data is still valid
  isCacheValid(cacheKey) {
    const expiry = this.cache[`${cacheKey}Expiry`];
    return expiry && Date.now() < expiry;
  }

  // Set cache data with TTL
  setCache(cacheKey, data) {
    this.cache[cacheKey] = data;
    this.cache[`${cacheKey}Expiry`] = Date.now() + this.cacheDurations[cacheKey];
    
    // Also store in localStorage for persistence across page reloads
    try {
      const cacheData = {
        data: data,
        expiry: this.cache[`${cacheKey}Expiry`]
      };
      localStorage.setItem(`btc_dashboard_${cacheKey}`, JSON.stringify(cacheData));
      console.log(`💾 Cached ${cacheKey} data for ${this.cacheDurations[cacheKey] / 1000}s`);
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
  }

  // Get cache data from memory or localStorage
  getCache(cacheKey) {
    // Check memory cache first
    if (this.isCacheValid(cacheKey)) {
      console.log(`🎯 Using memory cache for ${cacheKey}`);
      return this.cache[cacheKey];
    }

    // Check localStorage cache
    try {
      const stored = localStorage.getItem(`btc_dashboard_${cacheKey}`);
      if (stored) {
        const cacheData = JSON.parse(stored);
        if (Date.now() < cacheData.expiry) {
          console.log(`💾 Using localStorage cache for ${cacheKey}`);
          // Restore to memory cache
          this.cache[cacheKey] = cacheData.data;
          this.cache[`${cacheKey}Expiry`] = cacheData.expiry;
          return cacheData.data;
        } else {
          // Expired cache, remove it
          localStorage.removeItem(`btc_dashboard_${cacheKey}`);
        }
      }
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
    }

    return null;
  }

  // Fast method to get cached news from background service
  async fetchFromBackgroundCache() {
    try {
      // Try local static file first (served by web server)
      const response = await fetch('/api/cached-news.json', {
        headers: { 'Cache-Control': 'no-cache' }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`📦 Background cache: ${data.news?.length || 0} articles, updated: ${data.lastUpdated}`);
        return data.news || [];
      }
      
      throw new Error(`Cache file not available: ${response.status}`);
    } catch (error) {
      console.warn('Background cache fetch failed:', error.message);
      return null;
    }
  }

  // Fetch Bitcoin treasury related news with background cache
  async fetchBitcoinTreasuryNews() {
    // Check memory cache first (existing localStorage cache)
    const cachedNews = this.getCache('news');
    if (cachedNews) {
      return {
        success: true,
        data: cachedNews,
        cached: true
      };
    }

    // Try background cache service (fast!)
    try {
      console.log('📦 Checking background cache...');
      const backgroundCache = await this.fetchFromBackgroundCache();
      if (backgroundCache !== null) {
        // Cache the result locally too (even if empty - it's still valid cache)
        this.setCache('news', backgroundCache);
        console.log(`✅ Using background cache: ${backgroundCache.length} articles`);
        return {
          success: true,
          data: backgroundCache,
          source: 'background-cache'
        };
      }
    } catch (error) {
      console.warn('Background cache failed:', error.message);
    }

    console.log('🔄 Fetching fresh news data (direct API calls)...');
    const newsItems = [];
    const errors = [];

    try {
      // Try NewsAPI first (requires free API key)
      if (this.apiKeys.news_api) {
        try {
          console.log('Trying NewsAPI...');
          const newsApiData = await this.fetchFromNewsAPI();
          newsItems.push(...newsApiData);
          console.log(`✓ NewsAPI: ${newsApiData.length} articles`);
        } catch (error) {
          errors.push(`NewsAPI: ${error.message}`);
          console.warn('NewsAPI failed:', error.message);
        }
      } else {
        errors.push('NewsAPI: No API key configured');
        console.log('⚠️ NewsAPI: No API key configured');
      }

      // Try RSS feeds (free, no API key required)
      try {
        console.log('Trying RSS feeds...');
        const rssData = await this.fetchFromRSSFeeds();
        newsItems.push(...rssData);
        console.log(`✓ RSS Feeds: ${rssData.length} articles`);
      } catch (error) {
        errors.push(`RSS Feeds: ${error.message}`);
        console.warn('RSS Feeds failed:', error.message);
      }

      // Try CoinDesk API (free, no API key)
      try {
        console.log('Trying CoinDesk...');
        const coinDeskData = await this.fetchFromCoinDesk();
        newsItems.push(...coinDeskData);
        console.log(`✓ CoinDesk: ${coinDeskData.length} articles`);
      } catch (error) {
        errors.push(`CoinDesk: ${error.message}`);
        console.warn('CoinDesk failed:', error.message);
      }

    } catch (error) {
      errors.push(`General error: ${error.message}`);
      console.error('General news fetch error:', error);
    }

    if (newsItems.length === 0) {
      console.error('❌ All news sources failed');
      return {
        success: false,
        error: `No treasury-specific news available. API Errors: ${errors.join(' | ')}`,
        errorCode: 'NEWS_API_ALL_FAILED',
        data: []
      };
    }

    // Sort by timestamp and remove duplicates
    const uniqueNews = this.deduplicateNews(newsItems);
    
    // Apply treasury-specific filtering BEFORE caching
    const treasuryFilteredNews = uniqueNews.filter(article => {
      const fullText = (article.title || '') + ' ' + (article.summary || '');
      const isRelevant = this.isTreasuryRelevant(fullText, article.url || '');
      if (isRelevant) {
        console.log(`✅ TREASURY RELEVANT (cached): ${article.title}`);
      } else {
        console.log(`❌ TREASURY FILTERED OUT: ${article.title} | URL: ${article.url}`);
      }
      return isRelevant;
    });
    
    const sortedNews = treasuryFilteredNews.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    // Cache the filtered result
    this.setCache('news', sortedNews);

    console.log(`✅ Successfully fetched and cached ${sortedNews.length} news articles`);

    return {
      success: true,
      data: sortedNews,
      warnings: errors.length > 0 ? `Some sources failed: ${errors.join(' | ')}` : null
    };
  }

  // NewsAPI implementation
  async fetchFromNewsAPI() {
    if (!this.apiKeys.news_api) return [];

    try {
      const searchQuery = this.treasuryKeywords.join(' OR ');
      const response = await fetch(
        `https://newsapi.org/v2/everything?q=${encodeURIComponent(searchQuery)}&sortBy=publishedAt&language=en&apiKey=${this.apiKeys.news_api}`
      );

      if (response.ok) {
        const data = await response.json();
        const articles = data.articles?.map(article => ({
          id: `newsapi-${article.url}`,
          title: article.title,
          summary: article.description,
          url: article.url,
          timestamp: new Date(article.publishedAt),
          source: article.source.name,
          ticker: this.extractTickerFromText(article.title + ' ' + article.description),
          impact: this.analyzeImpact(article.title + ' ' + article.description),
          type: 'news'
        })) || [];
        
        // Filter for treasury relevance
        return articles.filter(article => {
          const fullText = (article.title || '') + ' ' + (article.summary || '');
          return this.isTreasuryRelevant(fullText, article.url);
        });
      }
    } catch (error) {
      console.error('NewsAPI error:', error);
    }
    return [];
  }

  // RSS feed parser using multiple CORS proxy methods
  async fetchFromRSSFeeds() {
    const newsItems = [];

    try {
      console.log('🔍 Starting RSS feed fetch...');
      // Try multiple RSS sources with AllOrigins as primary method (RSS2JSON having issues)
      const rssSources = [
        {
          name: 'CoinDesk',
          url: 'https://www.coindesk.com/arc/outboundfeeds/rss/',
          proxy: 'allorigins'
        },
        {
          name: 'Bitcoin Magazine',
          url: 'https://bitcoinmagazine.com/feed',
          proxy: 'allorigins'
        },
        {
          name: 'Decrypt',
          url: 'https://decrypt.co/feed',
          proxy: 'allorigins'
        },
        {
          name: 'CoinTelegraph Bitcoin',
          url: 'https://cointelegraph.com/rss/tag/bitcoin',
          proxy: 'allorigins'
        }
      ];

      console.log(`📡 Trying ${rssSources.length} RSS sources...`);

      for (const source of rssSources) {
        try {
          console.log(`🔄 Trying ${source.name} via ${source.proxy}...`);
          let articles = [];
          
          if (source.proxy === 'rss2json') {
            // Use RSS2JSON service with timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout
            
            try {
              const url = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(source.url)}`;
              console.log(`📡 RSS2JSON URL: ${url}`);
              
              const response = await fetch(url, { signal: controller.signal });
              clearTimeout(timeoutId);
              console.log(`📡 RSS2JSON Response status: ${response.status}`);
              
              if (response.ok) {
                const data = await response.json();
                console.log(`📡 RSS2JSON Response:`, data);
                if (data.status === 'ok' && data.items) {
                  articles = data.items.slice(0, 3); // Limit per source
                  console.log(`📰 RSS2JSON found ${articles.length} articles`);
                } else {
                  console.warn(`⚠️ RSS2JSON error: ${data.status}, message: ${data.message || 'Unknown error'}`);
                  // If RSS2JSON fails, try AllOrigins as fallback
                  throw new Error(`RSS2JSON failed: ${data.status}`);
                }
              } else {
                console.warn(`⚠️ RSS2JSON HTTP error: ${response.status}`);
                throw new Error(`RSS2JSON HTTP error: ${response.status}`);
              }
            } catch (error) {
              clearTimeout(timeoutId);
              console.warn(`⚠️ RSS2JSON failed for ${source.name}, trying AllOrigins fallback:`, error.message);
              
              // Fallback to AllOrigins
              try {
                const url = `https://api.allorigins.win/get?url=${encodeURIComponent(source.url)}`;
                console.log(`📡 AllOrigins fallback URL: ${url}`);
                
                const response = await fetch(url);
                if (response.ok) {
                  const data = await response.json();
                  console.log(`📡 AllOrigins fallback got ${data.contents?.length || 0} characters`);
                  articles = this.parseBasicRSS(data.contents, source.name);
                  console.log(`📰 AllOrigins fallback parsed ${articles.length} articles`);
                } else {
                  console.warn(`⚠️ AllOrigins fallback also failed: ${response.status}`);
                }
              } catch (fallbackError) {
                console.warn(`⚠️ Both RSS2JSON and AllOrigins failed for ${source.name}:`, fallbackError.message);
              }
            }
          } else {
            // Use AllOrigins CORS proxy with timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
            
            try {
              const url = `https://api.allorigins.win/get?url=${encodeURIComponent(source.url)}`;
              console.log(`📡 AllOrigins URL: ${url}`);
              
              const response = await fetch(url, { signal: controller.signal });
              clearTimeout(timeoutId);
              console.log(`📡 AllOrigins Response status: ${response.status}`);
              
              if (response.ok) {
                const data = await response.json();
                console.log(`📡 AllOrigins got ${data.contents?.length || 0} characters of content`);
                // Parse XML manually for basic RSS parsing
                articles = this.parseBasicRSS(data.contents, source.name);
                console.log(`📰 AllOrigins parsed ${articles.length} articles`);
              } else {
                console.warn(`⚠️ AllOrigins HTTP error: ${response.status}`);
              }
            } catch (error) {
              clearTimeout(timeoutId);
              console.warn(`⚠️ AllOrigins failed for ${source.name}:`, error.message);
            }
          }

          // Filter for Bitcoin treasury relevant content using strict filtering
          const relevantArticles = articles.filter(item => {
            const fullText = item.title + ' ' + (item.description || '');
            const articleUrl = item.link || item.url || '';
            const isRelevant = this.isTreasuryRelevant(fullText, articleUrl);
            if (isRelevant) {
              console.log(`✅ TREASURY RELEVANT: ${item.title}`);
            } else {
              console.log(`❌ NOT TREASURY RELEVANT: ${item.title} | URL: ${articleUrl}`);
            }
            return isRelevant;
          });

          relevantArticles.forEach((item, index) => {
            newsItems.push({
              id: `${source.name.toLowerCase().replace(/\s+/g, '-')}-${index}-${Date.now()}`,
              title: item.title,
              summary: this.cleanHtml(item.description || item.content || '').substring(0, 200) + '...',
              url: item.link || item.url || '#',
              timestamp: new Date(item.pubDate || item.published || Date.now()),
              source: source.name,
              ticker: this.extractTickerFromText(item.title + ' ' + (item.description || '')),
              impact: this.analyzeImpact(item.title + ' ' + (item.description || '')),
              type: 'rss'
            });
          });

          console.log(`✓ ${source.name}: Found ${relevantArticles.length} relevant articles`);
          
        } catch (error) {
          console.warn(`RSS source ${source.name} failed:`, error.message);
        }
      }

      // Try alternative approaches if RSS fails
      if (newsItems.length === 0) {
        try {
          console.log('🔄 Trying alternative crypto news API...');
          const alternativeNews = await this.fetchFromCryptoNewsAPI();
          newsItems.push(...alternativeNews);
        } catch (altError) {
          console.warn('Alternative crypto news API failed:', altError.message);
        }
        
        // No fallback content - only show real treasury news
      }

      console.log(`✓ Total news items collected from RSS: ${newsItems.length}`);

    } catch (error) {
      console.error('RSS feed error:', error);
      // Don't throw - just return empty array
    }

    return newsItems;
  }

  // Basic RSS XML parser for AllOrigins content
  parseBasicRSS(xmlContent, sourceName) {
    try {
      const items = [];
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
      
      const itemNodes = xmlDoc.querySelectorAll('item');
      
      itemNodes.forEach((item, index) => {
        if (index < 5) { // Limit to 5 items per source
          const title = item.querySelector('title')?.textContent || '';
          const description = item.querySelector('description')?.textContent || '';
          const link = item.querySelector('link')?.textContent || '';
          const pubDate = item.querySelector('pubDate')?.textContent || '';
          
          if (title) {
            items.push({
              title: title.trim(),
              description: description.trim(),
              link: link.trim(),
              pubDate: pubDate.trim()
            });
          }
        }
      });
      
      return items;
    } catch (error) {
      console.warn(`Failed to parse RSS for ${sourceName}:`, error);
      return [];
    }
  }

  // Alternative crypto news API
  async fetchFromCryptoNewsAPI() {
    try {
      // Use CoinGecko news API (free, no key required)
      const response = await fetch('https://api.coingecko.com/api/v3/news');
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.data) {
          const relevantNews = data.data
            .filter(item => {
              const fullText = item.title + ' ' + item.description;
              const isRelevant = this.isTreasuryRelevant(fullText, item.url || '');
              if (isRelevant) {
                console.log(`✅ COINGECKO TREASURY RELEVANT: ${item.title}`);
              }
              return isRelevant;
            })
            .slice(0, 5)
            .map(item => ({
              id: `coingecko-${item.id}`,
              title: item.title,
              summary: item.description.substring(0, 200) + '...',
              url: item.url,
              timestamp: new Date(item.created_at),
              source: 'CoinGecko News',
              ticker: this.extractTickerFromText(item.title + ' ' + item.description),
              impact: this.analyzeImpact(item.title + ' ' + item.description),
              type: 'api'
            }));
            
          console.log(`✓ CoinGecko News: Found ${relevantNews.length} relevant articles`);
          return relevantNews;
        }
      }
    } catch (error) {
      console.warn('CoinGecko News API failed:', error);
    }
    
    return [];
  }

  // Clean HTML tags from text
  cleanHtml(text) {
    return text.replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, ' ').trim();
  }


  // CoinDesk API - disabled as they don't have a proper news API
  async fetchFromCoinDesk() {
    // CoinDesk doesn't provide a public news API
    // Their RSS feed is handled in fetchFromRSSFeeds
    return [];
  }

  // Treasury-specific filtering function - STRICT filtering for corporate/treasury news only
  isTreasuryRelevant(text, url = '') {
    const lowerText = text.toLowerCase();
    const lowerUrl = url.toLowerCase();
    
    // Debug specific problem articles
    if (lowerUrl.includes('meteorite') || lowerText.includes('meteorite')) {
      console.log(`🚨 METEORITE ARTICLE DETECTED: "${text.substring(0, 100)}..." URL: ${url}`);
    }
    
    // Must contain "bitcoin" OR "btc"
    if (!lowerText.includes('bitcoin') && !lowerText.includes('btc')) {
      console.log(`❌ No Bitcoin mention: "${text.substring(0, 50)}..."`);
      return false;
    }
    
    // IMMEDIATE REJECTION for non-treasury patterns
    const rejectionPatterns = [
      'meteorite', 'mars', 'collectible', 'memorabilia', 'nft', 'loan', 'mortgage',
      'lending', 'borrow', 'personal finance', 'how to buy', 'guide', 'tutorial',
      'defi', 'yield farming', 'staking', 'mining reward', 'wallet', 'exchange',
      'trading', 'price prediction', 'technical analysis', 'chart analysis'
    ];
    
    for (const pattern of rejectionPatterns) {
      if (lowerText.includes(pattern) || lowerUrl.includes(pattern)) {
        console.log(`❌ Contains non-treasury pattern '${pattern}': ${url}`);
        return false;
      }
    }

    // Calculate treasury relevance score
    let score = 0;
    let matchedKeywords = [];

    // High value treasury keywords (MUST have these for corporate news)
    this.treasuryKeywords.forEach(keyword => {
      if (lowerText.includes(keyword.toLowerCase())) {
        score += 3;
        matchedKeywords.push(`treasury:${keyword}`);
      }
    });

    // Company-specific treasury mentions
    this.companyTreasuryKeywords.forEach(keyword => {
      if (lowerText.includes(keyword.toLowerCase())) {
        score += 2;
        matchedKeywords.push(`company:${keyword}`);
      }
    });

    // Known treasury companies mentioned (strong signal)
    this.btcTreasuryTickers.forEach(ticker => {
      if (lowerText.includes(ticker.toLowerCase()) || 
          lowerText.includes(`$${ticker.toLowerCase()}`)) {
        score += 2;
        matchedKeywords.push(`ticker:${ticker}`);
      }
    });

    // Treasury action words (but only if combined with treasury context)
    const hasTreasuryContext = this.treasuryKeywords.some(keyword => 
      lowerText.includes(keyword.toLowerCase())
    ) || this.companyTreasuryKeywords.some(keyword => 
      lowerText.includes(keyword.toLowerCase())
    );
    
    if (hasTreasuryContext) {
      this.treasuryActionKeywords.forEach(action => {
        if (lowerText.includes(action.toLowerCase())) {
          score += 1;
          matchedKeywords.push(`action:${action}`);
        }
      });
    }

    // STRICT exclusions for general crypto news and non-treasury content
    const excludeTerms = [
      'price prediction', 'technical analysis', 'chart', 'trading',
      'pump', 'dump', 'whale alert', 'exchange', 'defi', 'nft',
      'mining pool', 'hash rate', 'difficulty', 'mempool',
      'market analysis', 'crypto market', 'crypto news', 'price target',
      'resistance level', 'support level', 'buy signal', 'sell signal',
      'crypto trading', 'altcoin', 'ethereum', 'eth', 'crypto rally',
      'meteorite', 'space', 'mars', 'martian', 'asteroid', 'lunar',
      'collectible', 'auction', 'artifact', 'souvenir', 'memorabilia',
      'gaming', 'metaverse', 'virtual', 'nft', 'token sale',
      'airdrop', 'giveaway', 'contest', 'sweepstakes'
    ];

    let penaltyScore = 0;
    const excludedMatches = [];
    excludeTerms.forEach(term => {
      if (lowerText.includes(term.toLowerCase())) {
        penaltyScore += 3; // Higher penalty
        excludedMatches.push(term);
      }
    });

    const finalScore = Math.max(0, score - penaltyScore);
    
    console.log(`📊 TREASURY FILTER ANALYSIS for "${text.substring(0, 80)}..."`);
    console.log(`   Base score: ${score} (matched: ${matchedKeywords.join(', ')})`);
    console.log(`   Penalty: -${penaltyScore} (excluded: ${excludedMatches.join(', ')})`);
    console.log(`   Final score: ${finalScore} (threshold: 4)`);
    
    // STRICTER requirement: minimum score of 4 AND no major exclusions
    const isRelevant = finalScore >= 4 && penaltyScore === 0;
    console.log(`   Result: ${isRelevant ? '✅ TREASURY RELEVANT' : '❌ NOT TREASURY RELEVANT'}`);
    
    return isRelevant;
  }

  // Legacy function for backward compatibility
  containsBitcoinTreasuryKeywords(text) {
    return this.isTreasuryRelevant(text);
  }

  extractTickerFromText(text) {
    const upperText = text.toUpperCase();
    
    // Use word boundaries to prevent false matches (e.g., "COIN" in "Bitcoin")
    return this.btcTreasuryTickers.find(ticker => {
      // Create regex patterns for exact word matches
      const patterns = [
        new RegExp(`\\b${ticker}\\b`), // Word boundary match (COIN as standalone word)
        new RegExp(`\\$${ticker}\\b`), // Dollar symbol prefix ($COIN)
        new RegExp(`\\b${ticker}:`)    // Ticker with colon (COIN:)
      ];
      
      return patterns.some(pattern => pattern.test(upperText));
    }) || null;
  }

  analyzeImpact(text) {
    const lowerText = text.toLowerCase();
    
    const bullishWords = ['buy', 'purchase', 'acquire', 'invest', 'adopt', 'treasury', 'reserve', 'allocation'];
    const bearishWords = ['sell', 'divest', 'reduce', 'exit', 'concern', 'risk', 'drop'];
    
    const bullishCount = bullishWords.filter(word => lowerText.includes(word)).length;
    const bearishCount = bearishWords.filter(word => lowerText.includes(word)).length;
    
    if (bullishCount > bearishCount) return 'bullish';
    if (bearishCount > bullishCount) return 'bearish';
    return 'neutral';
  }

  deduplicateNews(newsItems) {
    const seen = new Set();
    return newsItems.filter(item => {
      const key = item.title?.substring(0, 50) || item.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  // Clear news cache (localStorage and memory)
  clearNewsCache() {
    try {
      // Clear localStorage cache
      localStorage.removeItem('btc_treasury_news');
      localStorage.removeItem('btc_treasury_news_expiry');
      console.log('🗑️ Cleared cached news data to apply new filtering');
    } catch (error) {
      console.warn('Could not clear localStorage cache:', error);
    }
  }

  // Method to set API keys
  setApiKey(service, key) {
    if (this.apiKeys.hasOwnProperty(service)) {
      this.apiKeys[service] = key;
      console.log(`API key set for ${service}`);
    } else {
      console.warn(`Unknown service: ${service}`);
    }
  }

  // Get latest Bitcoin treasury companies (detect new ones)
  async detectNewTreasuryCompanies() {
    try {
      const newsResult = await this.fetchBitcoinTreasuryNews();
      
      if (!newsResult.success) {
        return {
          success: false,
          error: `Failed to fetch news for company detection: ${newsResult.error}`,
          errorCode: 'NEWS_FETCH_FAILED',
          data: []
        };
      }
      
      // Look for companies announcing Bitcoin adoption
      const newCompanies = newsResult.data
        .filter(item => 
          item.impact === 'bullish' && 
          (item.title.toLowerCase().includes('announce') ||
           item.title.toLowerCase().includes('adopt') ||
           item.title.toLowerCase().includes('purchase'))
        )
        .map(item => ({
          ticker: item.ticker,
          company: this.extractCompanyName(item.title),
          announcement: item.title,
          timestamp: item.timestamp,
          source: item.source,
          url: item.url
        }))
        .filter(company => company.ticker && !this.btcTreasuryTickers.includes(company.ticker));

      return {
        success: true,
        data: newCompanies
      };
    } catch (error) {
      return {
        success: false,
        error: `Company detection failed: ${error.message}`,
        errorCode: 'COMPANY_DETECTION_FAILED',
        data: []
      };
    }
  }


  extractCompanyName(title) {
    // Simple extraction - in production would use NLP
    const words = title.split(' ');
    const companyWords = words.filter(word => 
      word.length > 3 && 
      word[0] === word[0].toUpperCase() &&
      !['Bitcoin', 'BTC', 'Crypto', 'Treasury'].includes(word)
    );
    return companyWords.slice(0, 2).join(' ');
  }
}

export default new RealDataService();